import type {
  FleetV2Response,
  GetV2WorkspacesWorkspaceidFleetsQueryParamsSortEnumKey,
  WorkspaceResponseV2,
} from '@emma/api_client/types';
import { Button, Icon, IconButton, Tooltip } from '@lego/klik-ui';
import { PlusBold, Trash } from '@lego/klik-ui/icons';
import { type MouseEvent, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { createColumnHelper } from '@/components/common/table/appTable';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useDeleteFleet, useFleets } from '@/hooks/fleet';
import { useCanWriteById } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { fleetTableColumns } from '../common/table/column_definitions/Fleet';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { CreateFleetDialog } from './CreateFleetDialog';

type Props = {
  workspace: WorkspaceResponseV2;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  name: 'nameLike',
  createdAt: ['createdAfter', 'createdBefore'],
};

const columnHelper = createColumnHelper<FleetV2Response>();

const DeleteFleetCell = ({ fleet }: { fleet: FleetV2Response }) => {
  const [isOpen, setIsOpen] = useState(false);
  const deleteFleet = useDeleteFleet(fleet.workspaceId, fleet.id);

  return (
    <>
      <Tooltip label="Delete fleet">
        <IconButton
          aria-label="Delete fleet"
          icon={<Trash />}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
          size="sm"
          variant="ghost"
        />
      </Tooltip>
      <ConfirmationDialog
        body="Are you sure you want to delete this fleet? Only empty fleets can be deleted."
        header="Delete Fleet"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirmed={() => {
          setIsOpen(false);
          void deleteFleet().catch(() => {});
        }}
      />
    </>
  );
};

const FleetTableContent = (props: Props) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWriteById(props.workspace.id);
  const { pagination, sorting, filters } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const columns = useMemo(() => {
    if (!canWrite) return fleetTableColumns;
    return columnHelper.columns([
      ...fleetTableColumns,
      columnHelper.display({
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => <DeleteFleetCell fleet={row.original} />,
      }),
    ]);
  }, [canWrite]);

  const queryParams = {
    ...serverTableParams<GetV2WorkspacesWorkspaceidFleetsQueryParamsSortEnumKey>(
      pagination,
      sorting
    ),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, mutate, isValidating } = useFleets(props.workspace.id, queryParams);

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <>
      <Table
        addButton={
          canWrite
            ? (props) => (
                <Tooltip label="Create a new fleet" placement="left">
                  <Button
                    {...props}
                    leftIcon={<Icon as={PlusBold} />}
                    onClick={() => setIsCreateDialogOpen(true)}
                    variant="outline"
                  >
                    Create
                  </Button>
                </Tooltip>
              )
            : undefined
        }
        columns={columns}
        data={data?.fleets || []}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        rowLink={({ id }) => paths.fleetById(props.workspace.id, id)}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Fleets"
      />
      <CreateFleetDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        workspaceId={props.workspace.id}
      />
    </>
  );
};

export const FleetTable = (props: Props) => (
  <TableStoreProvider
    initialSorting={[{ desc: true, id: 'id' }]}
    key={`Fleets-${props.workspace.id}`}
    name={`Fleets-${props.workspace.id}`}
  >
    <FleetTableContent {...props} />
  </TableStoreProvider>
);
